import { Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";

interface InitializationStatusSectionProps {
  status: string | undefined;
  loading: boolean;
  generatedAt: string;
  error: string | undefined;
  onInitialize: () => void;
  onReinitialize: () => void;
}

const OVERVIEW_BLURB =
  "An AI-generated summary of this dataset's tables and their contents, used by the analysis agent to navigate the data.";

const InitializationStatusSection = ({
  status,
  loading,
  generatedAt,
  error,
  onInitialize,
  onReinitialize,
}: InitializationStatusSectionProps) => {
  if (loading && !status) {
    return null;
  }

  if (status === "running") {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-sm text-muted-foreground">{OVERVIEW_BLURB}</p>
        <div className="flex items-center gap-1.5">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Analysis agent dataset context is being generated...
          </p>
        </div>
      </div>
    );
  }

  if (status === "completed") {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-sm text-muted-foreground">
          {OVERVIEW_BLURB} <strong>The overview is READ ONLY.</strong>
        </p>
        <p className="text-sm text-muted-foreground">
          Choose <strong>Update</strong> to incorporate newly added tables or resume an interrupted
          run, or <strong>Reinitialize</strong> to rebuild the overview from scratch.
        </p>
        <p className="text-sm text-muted-foreground">
          Analysis agent dataset context last generated: {generatedAt}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onInitialize}>
            Update
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="bg-gray-200 hover:bg-gray-300"
            onClick={onReinitialize}
          >
            Reinitialize
          </Button>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-sm text-muted-foreground">{OVERVIEW_BLURB}</p>
        <p className="text-sm text-muted-foreground">
          Use <strong>Retry</strong> to run initialization again.
        </p>
        <p className="text-sm text-destructive">Initialization failed{error ? `: ${error}` : ""}</p>
        <div>
          <Button variant="outline" size="sm" onClick={onInitialize}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm text-muted-foreground">{OVERVIEW_BLURB}</p>
      <p className="text-sm text-muted-foreground">
        Use <strong>Initialize</strong> to generate it for the first time.
      </p>
      <p className="text-sm text-muted-foreground">Dataset context has not been initialized.</p>
      <div>
        <Button variant="outline" size="sm" onClick={onInitialize}>
          Initialize
        </Button>
      </div>
    </div>
  );
};

export default InitializationStatusSection;
